import { useState } from "react";
import { setNewPassword } from "../api/set-new-password";

interface UseSetNewPasswordProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

export interface UseSetNewPasswordReturn {
  formData: FormData;
  errors: Partial<FormData>;
  submitError: string;
  isSubmitting: boolean;
  isSuccess: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  handleInputChange: (field: keyof FormData, value: string) => void;
  handleSubmit: (e: React.FormEvent, token: string | null) => void;
  toggleNewPasswordVisibility: () => void;
  toggleConfirmPasswordVisibility: () => void;
}

function useSetNewPassword({ onSuccess, onError }: UseSetNewPasswordProps): UseSetNewPasswordReturn {
  const [formData, setFormData] = useState<FormData>({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitError, setSubmitError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.newPassword) {
      newErrors.newPassword = "NEW_PASS_IS_REQUIRED";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "NEW_PASS_MIN_LENGTH";
    } else if (!/\d/.test(formData.newPassword) || !/[^A-Za-z0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = "NEW_PASS_NUMBER_SYMBOL";
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "CONFIRM_PASS_IS_REQUIRED";
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = "PASSWORDS_DO_NOT_MATCH";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError("");
  };

  const handleSubmit = async (e: React.FormEvent, token: string | null) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    if (!token) {
      setSubmitError("Invalid or missing token");
      onError && onError("Invalid or missing token");
      return;
    }
    setIsSubmitting(true);
    try {
      setIsSuccess(true);
      await setNewPassword(token, formData.newPassword);
      onSuccess && onSuccess();
    } catch (err: any) {
      setSubmitError("Failed to reset password");
      onError && onError("Failed to reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleNewPasswordVisibility = () => setShowNewPassword((v) => !v);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((v) => !v);

  return {
    formData,
    errors,
    submitError,
    isSubmitting,
    isSuccess,
    showNewPassword,
    showConfirmPassword,
    handleInputChange,
    handleSubmit,
    toggleNewPasswordVisibility,
    toggleConfirmPasswordVisibility,
  };
}

export default useSetNewPassword;
